import { useState } from 'react'
import { Filter, Calendar, X } from 'lucide-react'

export const SCAN_TYPES = [
  { value: 'test', label: 'Test' },
  { value: 'quiz', label: 'Quiz' },
  { value: 'homework', label: 'Homework' },
  { value: 'worksheet', label: 'Worksheet' },
  { value: 'classwork', label: 'Classwork' },
  { value: 'friday_folder', label: 'Friday Folder' },
  { value: 'other', label: 'Other' },
]

export const SUBJECTS = [
  'Math',
  'Reading',
  'Writing',
  'Science',
  'Social Studies',
  'Spanish',
  'Other',
]

interface ScanFiltersProps {
  onFilterChange: (filters: FilterState) => void
  initialFilters?: FilterState
  showUncategorizedToggle?: boolean
}

export interface FilterState {
  startDate: string
  endDate: string
  subject: string
  scanType: string
  uncategorized: boolean
}

export function ScanFilters({
  onFilterChange,
  initialFilters,
  showUncategorizedToggle = true,
}: ScanFiltersProps) {
  const [filters, setFilters] = useState<FilterState>(
    initialFilters || {
      startDate: '',
      endDate: '',
      subject: '',
      scanType: '',
      uncategorized: false,
    }
  )

  const [showFilters, setShowFilters] = useState(false)

  const handleChange = (key: keyof FilterState, value: string | boolean) => {
    const newFilters = { ...filters, [key]: value }
    setFilters(newFilters)
    onFilterChange(newFilters)
  }

  const clearFilters = () => {
    const emptyFilters: FilterState = {
      startDate: '',
      endDate: '',
      subject: '',
      scanType: '',
      uncategorized: false,
    }
    setFilters(emptyFilters)
    onFilterChange(emptyFilters)
  }

  const hasActiveFilters =
    filters.startDate ||
    filters.endDate ||
    filters.subject ||
    filters.scanType ||
    filters.uncategorized

  return (
    <div className="space-y-3">
      {/* Toggle button */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => setShowFilters(!showFilters)}
          className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${
            showFilters || hasActiveFilters
              ? 'bg-quest-primary/20 text-quest-primary'
              : 'bg-surface-card text-gray-400 hover:bg-surface-hover'
          }`}
        >
          <Filter className="w-4 h-4" />
          <span className="text-sm">
            {hasActiveFilters ? 'Filters Active' : 'Filters'}
          </span>
          {hasActiveFilters && (
            <span className="w-2 h-2 bg-quest-primary rounded-full" />
          )}
        </button>

        {hasActiveFilters && (
          <button
            onClick={clearFilters}
            className="flex items-center gap-1 px-2 py-1 text-sm text-gray-400 hover:text-white"
          >
            <X className="w-4 h-4" />
            Clear
          </button>
        )}
      </div>

      {/* Filter panel */}
      {showFilters && (
        <div className="bg-surface-card/50 rounded-lg p-4 space-y-4">
          {/* Date range */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-gray-400 mb-1">
                <Calendar className="w-3 h-3 inline mr-1" />
                From
              </label>
              <input
                type="date"
                value={filters.startDate}
                onChange={(e) => handleChange('startDate', e.target.value)}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">
                <Calendar className="w-3 h-3 inline mr-1" />
                To
              </label>
              <input
                type="date"
                value={filters.endDate}
                onChange={(e) => handleChange('endDate', e.target.value)}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm"
              />
            </div>
          </div>

          {/* Subject and Type */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-gray-400 mb-1">Subject</label>
              <select
                value={filters.subject}
                onChange={(e) => handleChange('subject', e.target.value)}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm"
              >
                <option value="">All Subjects</option>
                {SUBJECTS.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Type</label>
              <select
                value={filters.scanType}
                onChange={(e) => handleChange('scanType', e.target.value)}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm"
              >
                <option value="">All Types</option>
                {SCAN_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Uncategorized toggle */}
          {showUncategorizedToggle && (
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={filters.uncategorized}
                onChange={(e) => handleChange('uncategorized', e.target.checked)}
                className="w-4 h-4 rounded bg-gray-700 border-gray-600 text-quest-primary focus:ring-quest-primary"
              />
              <span className="text-sm text-gray-300">
                Show only uncategorized scans
              </span>
            </label>
          )}
        </div>
      )}
    </div>
  )
}
